import { supabase } from "@/integrations/supabase/client";
import type { Product, ProductImage, ProductVariant, ProductWithRelations, LegacyProduct } from "@/types/ecommerce";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export interface ListProductsParams {
  categorySlug?: string;
  collectionSlug?: string;
  featuredOnly?: boolean;
  bestsellerOnly?: boolean;
  activeOnly?: boolean;
  categoryId?: string;
  collectionId?: string;
  search?: string;
  status?: "all" | "active" | "inactive";
  withRelations?: boolean;
}

export interface AdminProductRow extends Product {
  images?: Pick<ProductImage, "id" | "image_url" | "is_primary" | "sort_order">[];
  variants?: Pick<ProductVariant, "id" | "stock_quantity" | "is_active">[];
  category_ref?: { id: string; name: string; slug: string } | null;
  collection_ref?: { id: string; name: string; slug: string } | null;
  total_stock?: number;
  primary_image_url?: string | null;
}

export const productsService = {
  async list(params: ListProductsParams = {}): Promise<Product[]> {
    const select = params.withRelations
      ? "*, images:product_images(id,image_url,is_primary,sort_order), variants:product_variants(id,price,stock_quantity,is_active,color:product_colors(id,name,slug,hex_code),size:product_sizes(id,name,slug)), category_ref:categories!products_category_id_fkey(id,name,slug)"
      : "*";
    let query = supabase.from("products").select(select).order("created_at", { ascending: false });
    if (params.activeOnly !== false) query = query.eq("is_active", true);
    if (params.featuredOnly) query = query.eq("is_featured", true);
    if ((params as any).bestsellerOnly) query = query.eq("is_bestseller", true);
    if (params.categoryId) query = query.eq("category_id", params.categoryId);
    if (params.collectionId) query = query.eq("collection_id", params.collectionId);
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as unknown as Product[];
  },

  async listAdmin(params: ListProductsParams = {}): Promise<AdminProductRow[]> {
    let query = supabase
      .from("products")
      .select(
        "*, images:product_images(id,image_url,is_primary,sort_order), variants:product_variants(id,stock_quantity,is_active), category_ref:categories!products_category_id_fkey(id,name,slug), collection_ref:collections!products_collection_id_fkey(id,name,slug)"
      )
      .order("created_at", { ascending: false });

    if (params.status === "active") query = query.eq("is_active", true);
    else if (params.status === "inactive") query = query.eq("is_active", false);

    if (params.categoryId) query = query.eq("category_id", params.categoryId);
    if (params.collectionId) query = query.eq("collection_id", params.collectionId);
    if (params.search?.trim()) query = query.ilike("name", `%${params.search.trim()}%`);

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map((row: any) => {
      const variants = row.variants ?? [];
      const total_stock = variants.reduce(
        (sum: number, v: any) => sum + (v.is_active === false ? 0 : v.stock_quantity ?? 0),
        0
      );
      const images = row.images ?? [];
      const primary =
        images.find((i: any) => i.is_primary)?.image_url ??
        images.sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0]?.image_url ??
        row.image_url ??
        null;
      return { ...row, total_stock, primary_image_url: primary } as AdminProductRow;
    });
  },

  async getById(id: string): Promise<ProductWithRelations | null> {
    const { data, error } = await supabase
      .from("products")
      .select("*, images:product_images(*), variants:product_variants(*, color:product_colors(*), size:product_sizes(*))")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as ProductWithRelations | null;
  },

  async getBySlug(slug: string): Promise<ProductWithRelations | null> {
    const { data, error } = await supabase
      .from("products")
      .select("*, images:product_images(*), variants:product_variants(*, color:product_colors(*), size:product_sizes(*))")
      .eq("slug", slug)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as ProductWithRelations | null;
  },

  async upsert(payload: (TablesInsert<"products"> | TablesUpdate<"products">) & { id?: string }) {
    const { id, ...rest } = payload;
    const { data, error } = id
      ? await supabase.from("products").update(rest as TablesUpdate<"products">).eq("id", id).select().single()
      : await supabase.from("products").insert(rest as TablesInsert<"products">).select().single();
    if (error) throw error;
    return data as unknown as Product;
  },

  async remove(id: string) {
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) throw error;
  },

  async addImage(image: Omit<ProductImage, "id">) {
    const { data, error } = await supabase.from("product_images").insert(image).select().single();
    if (error) throw error;
    return data as unknown as ProductImage;
  },

  async removeImage(id: string) {
    const { error } = await supabase.from("product_images").delete().eq("id", id);
    if (error) throw error;
  },

  async setPrimaryImage(productId: string, imageId: string) {
    const { error: clearError } = await supabase
      .from("product_images")
      .update({ is_primary: false })
      .eq("product_id", productId);
    if (clearError) throw clearError;
    const { error } = await supabase.from("product_images").update({ is_primary: true }).eq("id", imageId);
    if (error) throw error;
  },

  async removeVariant(id: string) {
    const { error } = await supabase.from("product_variants").delete().eq("id", id);
    if (error) throw error;
  },

  async upsertVariant(variant: (TablesInsert<"product_variants"> | TablesUpdate<"product_variants">) & { id?: string }) {
    const { id, ...rest } = variant;
    const { data, error } = id
      ? await supabase.from("product_variants").update(rest as TablesUpdate<"product_variants">).eq("id", id).select().single()
      : await supabase.from("product_variants").insert(rest as TablesInsert<"product_variants">).select().single();
    if (error) throw error;
    return data as unknown as ProductVariant;
  },
};

/**
 * Adapt the new Product shape to the legacy shape consumed by ProductCard/ProductGrid.
 * This keeps the existing UI working while we evolve the catalog.
 */
export const toLegacyProduct = (p: Product): LegacyProduct => ({
  id: p.id,
  name: p.name,
  price: Number(p.price),
  originalPrice: p.original_price ? Number(p.original_price) : null,
  image: p.image_url ?? "/placeholder.svg",
  category: p.category,
  badge: p.badge,
  slug: p.slug,
});

/**
 * Adapt a product fetched with relations (images + category_ref) to the legacy
 * shape, preferring the primary image and the relational category name.
 */
export const toLegacyProductWithRelations = (p: any): LegacyProduct => {
  const images = p.images ?? [];
  const primary =
    images.find((i: any) => i.is_primary)?.image_url ??
    [...images].sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0]?.image_url ??
    p.image_url ?? "/placeholder.svg";
  const variants: any[] = p.variants ?? [];
  const activeVariants = variants.filter((v) => v.is_active !== false);
  const variantPrices = activeVariants
    .map((v) => Number(v.price))
    .filter((n) => Number.isFinite(n) && n > 0);
  const productPrice = Number(p.price);
  const effectivePrice =
    Number.isFinite(productPrice) && productPrice > 0
      ? productPrice
      : variantPrices.length
        ? Math.min(...variantPrices)
        : 0;
  const totalStock = activeVariants.reduce(
    (sum, v) => sum + (Number(v.stock_quantity) || 0),
    0,
  );
  return {
    id: p.id,
    product_id: p.id,
    name: p.name,
    price: effectivePrice,
    originalPrice: p.original_price ? Number(p.original_price) : null,
    image: primary,
    category: p.category_ref?.name ?? p.category,
    badge: p.badge,
    slug: p.slug ?? null,
    is_bestseller: !!p.is_bestseller,
    total_stock: activeVariants.length ? totalStock : undefined,
  };
};

/**
 * Storage helper for product images. Returns the public URL.
 */
export const uploadProductImage = async (file: File): Promise<string> => {
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = `products/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from("product-images").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("product-images").getPublicUrl(path);
  return data.publicUrl;
};

export const slugify = (input: string): string =>
  input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");